import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Avatar,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { InstagramDMAutomation } from "../../../core/services/dm-automation-instagram.service";
import { FormValidation } from "../../../core/utility";
import { CashkaroService } from "../../../core/services";
import { BD } from "./CashkaroLayout";

// Same fetcher/validator the brand's own Add Product form and the influencer's
// Zap Storefront form already use — keeps "paste a URL, auto-fill the details"
// behaving identically everywhere it appears.
const zapProductService = new InstagramDMAutomation();
const formValidation = new FormValidation();

const CURRENCY_SYMBOLS: Record<string, string> = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" };

interface Props {
  open: boolean;
  onClose: () => void;
  campaignId: number;
  influencerId: number;
  onAdded: (product: any) => void;
}

const EMPTY_FORM = {
  product_title: "",
  product_description: "",
  product_url: "",
  price: "",
  currency: "INR",
  category: "",
  image_url: "",
};

// Lets an influencer add a product they found by actually browsing the brand's
// site (via the silently-opened tracked link on the campaign page) instead of
// only picking from the brand's own pre-selected list. Shares storefront_id
// with the brand's own catalog and auto-features into the same linked campaign
// server-side (Storefront.createProduct) — added_by_type just tags who added it.
export const AddCampaignProductDialog: React.FC<Props> = ({ open, onClose, campaignId, influencerId, onAdded }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [urlError, setUrlError] = useState("");
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [fetchStatus, setFetchStatus] = useState<"idle" | "success" | "failed">("idle");
  const [lastFetchedUrl, setLastFetchedUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const reset = () => {
    setForm(EMPTY_FORM);
    setUrlError("");
    setFetchingProduct(false);
    setFetchStatus("idle");
    setLastFetchedUrl("");
    setAlert(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const fetchProductDetails = async (rawUrl: string) => {
    const url = formValidation.NormalizeAffiliateLink(rawUrl);
    if (!url || url === lastFetchedUrl) return;
    const urlCheck = formValidation.GetAffiliateLinkValidate(url);
    if (!urlCheck.status) {
      setUrlError(urlCheck.message);
      return;
    }
    setUrlError("");
    setForm((f) => (f.product_url === url ? f : { ...f, product_url: url }));
    setLastFetchedUrl(url);
    setFetchingProduct(true);
    setFetchStatus("idle");
    try {
      const res: any = await zapProductService.fetchZapLinkProduct({ product_url: url, allow_other_source: true });
      if (res?.status === 1 && res?.data) {
        const d = res.data;
        setForm((f) => ({
          ...f,
          product_title: d.product_name || f.product_title,
          product_description: d.og_description || f.product_description,
          price: d.price !== null && d.price !== undefined && d.price !== "" ? String(d.price) : f.price,
          currency: d.currency || f.currency,
          image_url: d.product_image_url || f.image_url,
        }));
        setFetchStatus("success");
      } else {
        setFetchStatus("failed");
      }
    } catch {
      setFetchStatus("failed");
    } finally {
      setFetchingProduct(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.product_title || !form.product_url) {
      setAlert({ type: "error", msg: "Product name and product link are required" });
      return;
    }
    const urlCheck = formValidation.GetAffiliateLinkValidate(form.product_url);
    if (!urlCheck.status) {
      setUrlError(urlCheck.message);
      setAlert({ type: "error", msg: urlCheck.message });
      return;
    }
    setSaving(true);
    setAlert(null);
    try {
      const res = await CashkaroService.addCampaignProduct({
        campaign_id: campaignId,
        influencer_id: influencerId,
        product_title: form.product_title,
        product_description: form.product_description,
        product_url: formValidation.NormalizeAffiliateLink(form.product_url),
        price: form.price !== "" ? parseFloat(form.price) : undefined,
        currency: form.currency,
        category: form.category,
        image_url: form.image_url || undefined,
      });
      if (res?.status === 1) {
        const product = res.data?.product || null;
        onAdded(product);
        handleClose();
      } else {
        setAlert({ type: "error", msg: res?.info || "Failed to add product" });
      }
    } catch {
      setAlert({ type: "error", msg: "Something went wrong" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BD.radiusLg } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: BD.textPrimary }}>
            Add a product you found
          </Typography>
          <Typography variant="caption" sx={{ color: BD.textSecondary }}>
            Paste the product page URL you copied from the site — we'll fill in the details.
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: BD.textSecondary }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {alert && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
            {alert.msg}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Product URL"
            placeholder="https://brand.com/products/..."
            value={form.product_url}
            disabled={fetchingProduct}
            error={!!urlError}
            helperText={
              urlError
                ? urlError
                : fetchingProduct
                ? "Fetching product details…"
                : fetchStatus === "success"
                ? "Details auto-filled. Review and edit anything below if needed."
                : fetchStatus === "failed"
                ? "Couldn't fetch details automatically — please fill the fields below manually."
                : "Paste the link, then tap Auto-fill."
            }
            onChange={(e) => {
              const val = e.target.value;
              setForm((f) => ({ ...f, product_url: val }));
              setFetchStatus("idle");
              if (urlError) setUrlError("");
              if (val.trim() !== lastFetchedUrl) setLastFetchedUrl("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                fetchProductDetails(form.product_url);
              }
            }}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {fetchingProduct ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Button size="small" onClick={() => fetchProductDetails(form.product_url)} disabled={!form.product_url.trim()} sx={{ textTransform: "none" }}>
                      Auto-fill
                    </Button>
                  )}
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Avatar variant="rounded" src={form.image_url || undefined} sx={{ width: 56, height: 56, bgcolor: "#F5F5F5", flexShrink: 0 }}>
              {(form.product_title || "?")[0]}
            </Avatar>
            <TextField
              label="Image URL"
              placeholder="Auto-filled from the product link"
              value={form.image_url}
              disabled={fetchingProduct}
              onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>

          <TextField
            label="Product Name"
            value={form.product_title}
            disabled={fetchingProduct}
            onChange={(e) => setForm((f) => ({ ...f, product_title: e.target.value }))}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={form.product_description}
            disabled={fetchingProduct}
            onChange={(e) => setForm((f) => ({ ...f, product_description: e.target.value }))}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              label="Price"
              type="number"
              value={form.price}
              disabled={fetchingProduct}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOLS[form.currency] || form.currency}</InputAdornment>,
              }}
            />
            <TextField
              label="Category"
              value={form.category}
              disabled={fetchingProduct}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ textTransform: "none", color: BD.textSecondary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={saving || fetchingProduct}
          sx={{ bgcolor: BD.textPrimary, textTransform: "none", fontWeight: 600, borderRadius: BD.radiusSm, "&:hover": { bgcolor: "#000" } }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Add Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
